import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  Plus,
  Play,
  Pencil,
  Trash2,
  Copy,
  BarChart3,
  PhoneOutgoing,
  ClipboardList,
  MessageSquare,
  RefreshCw,
  CheckCircle,
  XCircle,
  Clock,
  ChevronUp,
  ChevronDown
} from 'lucide-react';
import api from '../services/api';

// Script type badges
const SCRIPT_TYPES = {
  csat: { label: 'CSAT Survey', color: 'bg-green-100 text-green-800' },
  outbound: { label: 'Outbound', color: 'bg-blue-100 text-blue-800' },
  survey: { label: 'Survey', color: 'bg-purple-100 text-purple-800' },
  reminder: { label: 'Reminder', color: 'bg-yellow-100 text-yellow-800' },
  notification: { label: 'Notification', color: 'bg-orange-100 text-orange-800' }
};

// Step type icons and colors
const STEP_TYPES = {
  greeting: { icon: MessageSquare, color: 'bg-blue-500' },
  message: { icon: MessageSquare, color: 'bg-gray-500' },
  question: { icon: ClipboardList, color: 'bg-purple-500' },
  satisfaction: { icon: BarChart3, color: 'bg-green-500' },
  confirmation: { icon: CheckCircle, color: 'bg-teal-500' },
  condition: { icon: RefreshCw, color: 'bg-yellow-500' },
  transfer: { icon: PhoneOutgoing, color: 'bg-orange-500' },
  api: { icon: RefreshCw, color: 'bg-indigo-500' },
  end: { icon: XCircle, color: 'bg-red-500' }
};

export default function Scripts() {
  const { agentId } = useParams();
  const navigate = useNavigate();
  
  const [scripts, setScripts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedScript, setSelectedScript] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [agent, setAgent] = useState(null);

  useEffect(() => {
    loadAgent();
    loadScripts();
  }, [agentId]);

  const loadAgent = async () => {
    try {
      const response = await api.get(`/agents/${agentId}`);
      setAgent(response.data.data || response.data);
    } catch (error) {
      console.error('Error loading agent:', error);
    }
  };

  const loadScripts = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/scripts/${agentId}`);
      setScripts(response.data.data || []);
    } catch (error) {
      console.error('Error loading scripts:', error);
      toast.error('Failed to load scripts');
    } finally {
      setLoading(false);
    }
  };

  const loadScriptDetails = async (scriptId) => {
    try {
      const response = await api.get(`/scripts/${agentId}/${scriptId}`);
      setSelectedScript(response.data.data);
      setShowEditor(true);
    } catch (error) {
      console.error('Error loading script:', error);
      toast.error('Failed to load script details');
    }
  };

  const loadAnalytics = async (scriptId) => {
    try {
      const response = await api.get(`/scripts/${agentId}/${scriptId}/analytics`);
      setAnalytics(response.data.data);
      setShowAnalytics(true);
    } catch (error) {
      console.error('Error loading analytics:', error);
      toast.error('Failed to load analytics');
    }
  };

  const deleteScript = async (scriptId) => {
    if (!confirm('Are you sure you want to delete this script?')) return;
    
    try {
      await api.delete(`/scripts/${agentId}/${scriptId}`);
      toast.success('Script deleted');
      loadScripts();
    } catch (error) {
      console.error('Error deleting script:', error);
      toast.error('Failed to delete script');
    }
  };

  const duplicateScript = async (script) => {
    try {
      const newScript = {
        ...script,
        name: `${script.name} (Copy)`,
        script_key: `${script.script_key}_copy_${Date.now()}`
      };
      delete newScript.id;
      delete newScript.created_at;
      delete newScript.updated_at;
      
      await api.post(`/scripts/${agentId}`, newScript);
      toast.success('Script duplicated');
      loadScripts();
    } catch (error) {
      console.error('Error duplicating script:', error);
      toast.error('Failed to duplicate script');
    }
  };

  const createNewScript = () => {
    setSelectedScript({
      name: '',
      script_key: '',
      description: '',
      script_type: 'outbound',
      variable_schema: {},
      completion_api: '',
      auto_end: true,
      goodbye_message: 'Thank you for your time. Goodbye!',
      steps: []
    });
    setShowEditor(true);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Outbound Scripts</h1>
          <p className="text-gray-500 mt-1">
            {agent?.name || 'Agent'} - Manage call scripts for CSAT surveys, reminders, and outbound campaigns
          </p>
        </div>
        <button
          onClick={createNewScript}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          <Plus className="w-5 h-5" />
          New Script
        </button>
      </div>

      {/* Scripts List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-4 border-primary-500/30 border-t-primary-500 rounded-full animate-spin" />
        </div>
      ) : scripts.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <ClipboardList className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Scripts Yet</h3>
          <p className="text-gray-500 mb-4">
            Create your first outbound call script for CSAT surveys, reminders, or campaigns.
          </p>
          <button
            onClick={createNewScript}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            Create First Script
          </button>
        </div>
      ) : (
        <div className="grid gap-4">
          {scripts.map((script) => (
            <div
              key={script.id}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">{script.name}</h3>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${SCRIPT_TYPES[script.script_type]?.color || 'bg-gray-100 text-gray-800'}`}>
                      {SCRIPT_TYPES[script.script_type]?.label || script.script_type}
                    </span>
                  </div>
                  <p className="text-gray-500 text-sm mb-3">{script.description || 'No description'}</p>
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <span className="flex items-center gap-1">
                      <ClipboardList className="w-4 h-4" />
                      {script.steps?.length || 0} steps
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {new Date(script.updated_at || script.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => loadAnalytics(script.id)}
                    className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg"
                    title="View Analytics"
                  >
                    <BarChart3 className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => loadScriptDetails(script.id)}
                    className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg"
                    title="Edit Script"
                  >
                    <Pencil className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => duplicateScript(script)}
                    className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg"
                    title="Duplicate Script"
                  >
                    <Copy className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => deleteScript(script.id)}
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                    title="Delete Script"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Script Editor Modal */}
      {showEditor && selectedScript && (
        <ScriptEditor
          script={selectedScript}
          agentId={agentId}
          onClose={() => {
            setShowEditor(false);
            setSelectedScript(null);
          }}
          onSave={() => {
            setShowEditor(false);
            setSelectedScript(null);
            loadScripts();
          }}
        />
      )}

      {/* Analytics Modal */}
      {showAnalytics && analytics && (
        <ScriptAnalytics
          analytics={analytics}
          onClose={() => {
            setShowAnalytics(false);
            setAnalytics(null);
          }}
        />
      )}
    </div>
  );
}

// Script Editor Component
function ScriptEditor({ script, agentId, onClose, onSave }) {
  const [formData, setFormData] = useState(script);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');

  const handleSave = async () => {
    if (!formData.name || !formData.script_key) {
      toast.error('Name and Script Key are required');
      return;
    }

    try {
      setSaving(true);
      
      if (script.id) {
        await api.put(`/scripts/${agentId}/${script.id}`, formData);
        toast.success('Script updated');
      } else {
        await api.post(`/scripts/${agentId}`, formData);
        toast.success('Script created');
      }
      
      onSave();
    } catch (error) {
      console.error('Error saving script:', error);
      toast.error('Failed to save script');
    } finally {
      setSaving(false);
    }
  };

  const addStep = () => {
    setFormData(prev => ({
      ...prev,
      steps: [...(prev.steps || []), {
        step_order: (prev.steps?.length || 0) + 1,
        name: '',
        step_type: 'message',
        message: '',
        wait_for_response: false
      }]
    }));
  };

  const updateStep = (index, updates) => {
    setFormData(prev => ({
      ...prev,
      steps: prev.steps.map((step, i) => i === index ? { ...step, ...updates } : step)
    }));
  };

  const removeStep = (index) => {
    setFormData(prev => ({
      ...prev,
      steps: prev.steps.filter((_, i) => i !== index).map((step, i) => ({ ...step, step_order: i + 1 }))
    }));
  };

  const moveStep = (index, direction) => {
    const newSteps = [...formData.steps];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= newSteps.length) return;
    
    [newSteps[index], newSteps[newIndex]] = [newSteps[newIndex], newSteps[index]];
    newSteps.forEach((step, i) => step.step_order = i + 1);
    
    setFormData(prev => ({ ...prev, steps: newSteps }));
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-semibold">
            {script.id ? 'Edit Script' : 'New Script'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XCircle className="w-6 h-6" />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 px-6">
          <div className="flex gap-4">
            {['basic', 'variables', 'steps'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-3 px-1 border-b-2 font-medium text-sm capitalize ${
                  activeTab === tab
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'basic' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    placeholder="CSAT Post-Call Survey"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Script Key *</label>
                  <input
                    type="text"
                    value={formData.script_key}
                    onChange={(e) => setFormData(prev => ({ ...prev, script_key: e.target.value.toLowerCase().replace(/\s+/g, '_') }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    placeholder="csat_post_call"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={formData.description || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  rows={2}
                  placeholder="Customer satisfaction survey after agent interaction"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Script Type</label>
                  <select
                    value={formData.script_type}
                    onChange={(e) => setFormData(prev => ({ ...prev, script_type: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="outbound">Outbound</option>
                    <option value="csat">CSAT Survey</option>
                    <option value="survey">Survey</option>
                    <option value="reminder">Reminder</option>
                    <option value="notification">Notification</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Completion API</label>
                  <input
                    type="text"
                    value={formData.completion_api || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, completion_api: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    placeholder="https://api.example.com/csat/submit"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Goodbye Message</label>
                <input
                  type="text"
                  value={formData.goodbye_message || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, goodbye_message: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="Thank you for your time, {customer_name}. Goodbye!"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="auto_end"
                  checked={formData.auto_end !== false}
                  onChange={(e) => setFormData(prev => ({ ...prev, auto_end: e.target.checked }))}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <label htmlFor="auto_end" className="text-sm text-gray-700">
                  Automatically end call after script completes
                </label>
              </div>
            </div>
          )}

          {activeTab === 'variables' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-500 mb-4">
                Define variables that will be passed when starting this script. Use them in messages as {'{variable_name}'}.
              </p>
              
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium mb-2">Quick Add Common Variables</h4>
                <div className="flex flex-wrap gap-2">
                  {['customer_name', 'agent_name', 'interaction_date', 'ticket_id', 'phone_number', 'account_number', 'amount_due', 'due_date'].map((v) => (
                    <button
                      key={v}
                      onClick={() => {
                        if (!formData.variable_schema?.[v]) {
                          setFormData(prev => ({
                            ...prev,
                            variable_schema: {
                              ...prev.variable_schema,
                              [v]: { type: 'string', required: false, description: v.replace(/_/g, ' ') }
                            }
                          }));
                        }
                      }}
                      className={`px-2 py-1 rounded border text-sm ${formData.variable_schema?.[v] ? 'bg-green-100 border-green-300 text-green-700' : 'bg-white hover:bg-gray-100'}`}
                      disabled={!!formData.variable_schema?.[v]}
                    >
                      {`{${v}}`} {formData.variable_schema?.[v] && '✓'}
                    </button>
                  ))}
                </div>
              </div>

              <div className="border rounded-lg divide-y">
                <div className="grid grid-cols-12 gap-2 px-4 py-2 bg-gray-50 text-sm font-medium text-gray-600">
                  <div className="col-span-3">Variable Name</div>
                  <div className="col-span-2">Type</div>
                  <div className="col-span-4">Description</div>
                  <div className="col-span-2">Required</div>
                  <div className="col-span-1">Action</div>
                </div>
                
                {Object.entries(formData.variable_schema || {}).map(([key, value]) => (
                  <div key={key} className="grid grid-cols-12 gap-2 px-4 py-2 items-center">
                    <div className="col-span-3">
                      <code className="text-sm bg-gray-100 px-2 py-1 rounded">{`{${key}}`}</code>
                    </div>
                    <div className="col-span-2">
                      <select
                        value={value.type || 'string'}
                        onChange={(e) => {
                          setFormData(prev => ({
                            ...prev,
                            variable_schema: {
                              ...prev.variable_schema,
                              [key]: { ...value, type: e.target.value }
                            }
                          }));
                        }}
                        className="w-full px-2 py-1 border rounded text-sm"
                      >
                        <option value="string">String</option>
                        <option value="number">Number</option>
                        <option value="date">Date</option>
                      </select>
                    </div>
                    <div className="col-span-4">
                      <input
                        type="text"
                        value={value.description || ''}
                        onChange={(e) => {
                          setFormData(prev => ({
                            ...prev,
                            variable_schema: {
                              ...prev.variable_schema,
                              [key]: { ...value, description: e.target.value }
                            }
                          }));
                        }}
                        className="w-full px-2 py-1 border rounded text-sm"
                        placeholder="Description..."
                      />
                    </div>
                    <div className="col-span-2 flex items-center">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={value.required || false}
                          onChange={(e) => {
                            setFormData(prev => ({
                              ...prev,
                              variable_schema: {
                                ...prev.variable_schema,
                                [key]: { ...value, required: e.target.checked }
                              }
                            }));
                          }}
                          className="rounded"
                        />
                        <span className="text-sm text-gray-600">Required</span>
                      </label>
                    </div>
                    <div className="col-span-1">
                      <button
                        onClick={() => {
                          const newSchema = { ...formData.variable_schema };
                          delete newSchema[key];
                          setFormData(prev => ({ ...prev, variable_schema: newSchema }));
                        }}
                        className="p-1 text-red-500 hover:bg-red-50 rounded"
                        title="Remove variable"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
                
                {Object.keys(formData.variable_schema || {}).length === 0 && (
                  <div className="px-4 py-6 text-center text-gray-500 text-sm">
                    No variables defined. Click a common variable above or add a custom one below.
                  </div>
                )}
              </div>

              {/* Add custom variable */}
              <div className="flex gap-2 items-end">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Add Custom Variable</label>
                  <input
                    type="text"
                    id="new-variable-name"
                    placeholder="variable_name (no spaces)"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        const input = e.target;
                        const name = input.value.trim().toLowerCase().replace(/\s+/g, '_');
                        if (name && !formData.variable_schema?.[name]) {
                          setFormData(prev => ({
                            ...prev,
                            variable_schema: {
                              ...prev.variable_schema,
                              [name]: { type: 'string', required: false, description: '' }
                            }
                          }));
                          input.value = '';
                        }
                      }
                    }}
                  />
                </div>
                <button
                  onClick={() => {
                    const input = document.getElementById('new-variable-name');
                    const name = input.value.trim().toLowerCase().replace(/\s+/g, '_');
                    if (name && !formData.variable_schema?.[name]) {
                      setFormData(prev => ({
                        ...prev,
                        variable_schema: {
                          ...prev.variable_schema,
                          [name]: { type: 'string', required: false, description: '' }
                        }
                      }));
                      input.value = '';
                    }
                  }}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm"
                >
                  Add Variable
                </button>
              </div>

              {/* Raw JSON toggle for advanced users */}
              <details className="mt-4">
                <summary className="text-sm text-gray-500 cursor-pointer hover:text-gray-700">
                  Advanced: Edit Raw JSON
                </summary>
                <textarea
                  value={JSON.stringify(formData.variable_schema || {}, null, 2)}
                  onChange={(e) => {
                    try {
                      setFormData(prev => ({ ...prev, variable_schema: JSON.parse(e.target.value) }));
                    } catch (err) {
                      // Invalid JSON, ignore
                    }
                  }}
                  className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 font-mono text-sm"
                  rows={6}
                />
              </details>
            </div>
          )}

          {activeTab === 'steps' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <p className="text-sm text-gray-500">
                  Define the sequence of steps for this script.
                </p>
                <button
                  onClick={addStep}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  <Plus className="w-4 h-4" />
                  Add Step
                </button>
              </div>

              {formData.steps?.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No steps yet. Click "Add Step" to begin.
                </div>
              ) : (
                <div className="space-y-3">
                  {formData.steps?.map((step, index) => (
                    <StepEditor
                      key={index}
                      step={step}
                      index={index}
                      totalSteps={formData.steps.length}
                      onChange={(updates) => updateStep(index, updates)}
                      onRemove={() => removeStep(index)}
                      onMove={(dir) => moveStep(index, dir)}
                      allStepNames={formData.steps.map(s => s.name).filter(Boolean)}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Script'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Step Editor Component
function StepEditor({ step, index, totalSteps, onChange, onRemove, onMove, allStepNames }) {
  const [expanded, setExpanded] = useState(false);
  const StepIcon = STEP_TYPES[step.step_type]?.icon || MessageSquare;
  const stepColor = STEP_TYPES[step.step_type]?.color || 'bg-gray-500';

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      {/* Step Header */}
      <div 
        className="flex items-center gap-3 p-3 bg-gray-50 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className={`w-8 h-8 ${stepColor} rounded-lg flex items-center justify-center text-white`}>
          <StepIcon className="w-4 h-4" />
        </div>
        <div className="flex-1">
          <span className="font-medium">{step.name || `Step ${index + 1}`}</span>
          <span className="text-gray-500 text-sm ml-2">({step.step_type})</span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={(e) => { e.stopPropagation(); onMove(-1); }}
            disabled={index === 0}
            className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
          >
            ↑
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onMove(1); }}
            disabled={index === totalSteps - 1}
            className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
          >
            ↓
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onRemove(); }}
            className="p-1 text-red-400 hover:text-red-600"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Step Details */}
      {expanded && (
        <div className="p-4 space-y-3 border-t border-gray-200">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Step Name</label>
              <input
                type="text"
                value={step.name || ''}
                onChange={(e) => onChange({ name: e.target.value })}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary-500"
                placeholder="greeting"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Step Type</label>
              <select
                value={step.step_type}
                onChange={(e) => onChange({ step_type: e.target.value })}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary-500"
              >
                <option value="greeting">Greeting</option>
                <option value="message">Message</option>
                <option value="question">Question</option>
                <option value="satisfaction">Satisfaction Rating</option>
                <option value="confirmation">Confirmation</option>
                <option value="condition">Condition</option>
                <option value="transfer">Transfer</option>
                <option value="api">API Call</option>
                <option value="end">End Call</option>
              </select>
            </div>
            <div className="flex items-end gap-2">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={step.wait_for_response !== false}
                  onChange={(e) => onChange({ wait_for_response: e.target.checked })}
                  className="rounded border-gray-300 text-primary-600"
                />
                Wait for response
              </label>
            </div>
          </div>

          {/* Type-specific fields */}
          {['greeting', 'message', 'end'].includes(step.step_type) && (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Message</label>
              <textarea
                value={step.message || ''}
                onChange={(e) => onChange({ message: e.target.value })}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary-500"
                rows={2}
                placeholder="Hello {customer_name}, this is a quick call from..."
              />
            </div>
          )}

          {['question', 'satisfaction'].includes(step.step_type) && (
            <>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Question</label>
                <textarea
                  value={step.question || ''}
                  onChange={(e) => onChange({ question: e.target.value })}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary-500"
                  rows={2}
                  placeholder="On a scale of 1 to 5, how satisfied were you?"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Slot Name</label>
                  <input
                    type="text"
                    value={step.slot_name || ''}
                    onChange={(e) => onChange({ slot_name: e.target.value })}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary-500"
                    placeholder="satisfaction_score"
                  />
                </div>
                {step.step_type === 'satisfaction' && (
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Low Score Follow-up</label>
                    <input
                      type="text"
                      value={step.low_score_question || ''}
                      onChange={(e) => onChange({ low_score_question: e.target.value })}
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary-500"
                      placeholder="What could we improve?"
                    />
                  </div>
                )}
              </div>
            </>
          )}

          {step.step_type === 'condition' && (
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Condition</label>
                <input
                  type="text"
                  value={step.condition_expr || ''}
                  onChange={(e) => onChange({ condition_expr: e.target.value })}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary-500"
                  placeholder="satisfaction >= 4"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Then Goto</label>
                <select
                  value={step.then_goto || ''}
                  onChange={(e) => onChange({ then_goto: e.target.value })}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary-500"
                >
                  <option value="">Next step</option>
                  {allStepNames.map((name) => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Else Goto</label>
                <select
                  value={step.else_goto || ''}
                  onChange={(e) => onChange({ else_goto: e.target.value })}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary-500"
                >
                  <option value="">Next step</option>
                  {allStepNames.map((name) => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {step.step_type === 'transfer' && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Message</label>
                <input
                  type="text"
                  value={step.message || ''}
                  onChange={(e) => onChange({ message: e.target.value })}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary-500"
                  placeholder="Let me transfer you..."
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Transfer Queue</label>
                <input
                  type="text"
                  value={step.transfer_queue || ''}
                  onChange={(e) => onChange({ transfer_queue: e.target.value })}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary-500"
                  placeholder="support"
                />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Analytics Modal
function ScriptAnalytics({ analytics, onClose }) {
  const summary = analytics.summary || {};
  const satisfactionDist = analytics.satisfaction_distribution || [];
  const dailyTrend = analytics.daily_trend || [];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-semibold">Script Analytics</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XCircle className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto space-y-6">
          {/* Summary Stats */}
          <div className="grid grid-cols-4 gap-4">
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-gray-900">{summary.total_executions || 0}</div>
              <div className="text-sm text-gray-500">Total Calls</div>
            </div>
            <div className="bg-green-50 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-green-600">{summary.completed || 0}</div>
              <div className="text-sm text-gray-500">Completed</div>
            </div>
            <div className="bg-blue-50 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-blue-600">
                {summary.avg_satisfaction ? summary.avg_satisfaction.toFixed(1) : 'N/A'}
              </div>
              <div className="text-sm text-gray-500">Avg Satisfaction</div>
            </div>
            <div className="bg-purple-50 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-purple-600">
                {summary.avg_completion_rate ? `${summary.avg_completion_rate.toFixed(0)}%` : 'N/A'}
              </div>
              <div className="text-sm text-gray-500">Completion Rate</div>
            </div>
          </div>

          {/* Satisfaction Distribution */}
          {satisfactionDist.length > 0 && (
            <div>
              <h3 className="font-medium mb-3">Satisfaction Distribution</h3>
              <div className="flex items-end gap-2 h-32">
                {[1, 2, 3, 4, 5].map((score) => {
                  const data = satisfactionDist.find(d => d.satisfaction_score === score);
                  const count = data?.count || 0;
                  const maxCount = Math.max(...satisfactionDist.map(d => d.count), 1);
                  const height = (count / maxCount) * 100;
                  
                  return (
                    <div key={score} className="flex-1 flex flex-col items-center">
                      <div className="text-sm text-gray-600 mb-1">{count}</div>
                      <div 
                        className={`w-full rounded-t ${score <= 2 ? 'bg-red-400' : score === 3 ? 'bg-yellow-400' : 'bg-green-400'}`}
                        style={{ height: `${height}%`, minHeight: count > 0 ? '8px' : '0' }}
                      />
                      <div className="text-sm text-gray-500 mt-1">{score}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Daily Trend */}
          {dailyTrend.length > 0 && (
            <div>
              <h3 className="font-medium mb-3">Recent Activity</h3>
              <div className="space-y-2">
                {dailyTrend.slice(0, 7).map((day) => (
                  <div key={day.date} className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">{new Date(day.date).toLocaleDateString()}</span>
                    <span>{day.executions} calls</span>
                    <span className="text-gray-500">
                      {day.avg_satisfaction ? `Avg: ${day.avg_satisfaction.toFixed(1)}` : '-'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}